/*
  Iron Neck PLP – custom elements used by sections/plp-*.liquid
  Loaded as a module from snippets/plp-assets.liquid, so it runs once per page.
*/

const mobileQuery = window.matchMedia('(max-width: 749px)');

/* Trust strip: shows one item at a time on mobile (desktop shows them all). */
class PlpRotator extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-rotator-item]'));
    if (this.items.length < 2) return;

    this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
    this.paused = false;

    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = false));
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.items.indexOf(event.target);
      if (index === -1) return;
      this.paused = true;
      this.show(index);
    });
    this.addEventListener('shopify:block:deselect', () => (this.paused = false));

    this.timer = setInterval(() => {
      if (this.paused || document.hidden || !mobileQuery.matches || document.documentElement.classList.contains('a11y-motion-paused')) return;
      this.show((this.index + 1) % this.items.length);
    }, Number(this.dataset.interval) || 3500);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  show(index) {
    this.items[this.index].classList.remove('is-active');
    this.index = index;
    this.items[index].classList.add('is-active');
  }
}

/* Scroll-snap slider with prev/next buttons (bundles). Buttons hide when nothing overflows. */
class PlpSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    this.controls = this.querySelector('[data-slider-controls]');
    this.update = this.update.bind(this);

    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', this.update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);

    this.addEventListener('shopify:block:select', (event) => {
      event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  go(direction) {
    const slide = this.track.querySelector('[data-slider-slide]');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = slide ? slide.offsetWidth + gap : this.track.clientWidth;
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;
    if (this.controls) this.controls.hidden = maxScroll <= 1;
    if (this.prev) this.prev.disabled = scrollLeft <= 1;
    if (this.next) this.next.disabled = scrollLeft >= maxScroll - 1;
  }
}

/*
  Video reel (customer reviews): a coverflow on top of native scroll-snap.
  - On every scroll frame each slide gets a scale and inward shift from its distance to the
    centre (transform only), so touch swipes stay native and smooth, one video per swipe.
  - Mouse: drag with momentum. Arrows, dots and the arrow keys wrap around.
  - The centred video plays muted (data-autoplay). Tapping it or the speaker turns sound on,
    and sound stays on for the next videos. data-advance plays the next video when one ends.
  - Progress bar, loading spinner and full screen. Videos pause off screen or in a hidden tab.
*/
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class PlpReel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-reel-slide]'));
    if (!this.track || !this.slides.length) return;

    // Cards carry the transform; slides stay untransformed so snap points don't move.
    this.cards = this.slides.map((slide) => slide.querySelector('[data-reel-card]') || slide);
    this.videos = this.slides.map((slide) => slide.querySelector('video'));
    this.dots = Array.from(this.querySelectorAll('[data-reel-dot]'));
    this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches;
    this.advance = this.dataset.advance === 'true' && this.slides.length > 1;
    this.active = -1;
    this.inView = false;
    this.soundOn = false;
    this.userStarted = false;

    this.onScroll = () => this.requestRender();
    this.onVisibility = () => this.syncPlayback();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);

    this.bindSlides();
    this.bindControls();
    this.bindDrag();

    this.resizeObserver = new ResizeObserver(() => {
      this.measure();
      if (this.active > -1) this.goTo(this.active, 'auto');
      this.render();
    });
    this.resizeObserver.observe(this.track);

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        this.syncPlayback();
      },
      { threshold: 0.3 }
    );
    this.intersectionObserver.observe(this);

    // Start on the slide marked active in the markup (the middle one).
    this.measure();
    const initial = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.goTo(Math.max(initial, 0), 'auto');
    this.render();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.progressFrame);
    clearTimeout(this.settleTimer);
  }

  measure() {
    this.centers = this.slides.map((slide) => slide.offsetLeft + slide.offsetWidth / 2);
    this.width = this.slides[0].offsetWidth;
    this.step = this.slides.length > 1 ? this.centers[1] - this.centers[0] : this.width;
    const minScale = parseFloat(getComputedStyle(this).getPropertyValue('--reel-min-scale')) || 0.866;
    this.shrink = 1 - minScale;
  }

  requestRender() {
    if (!this.frame) this.frame = requestAnimationFrame(() => this.render());
  }

  // Scale each slide by its distance from the centre and pull it inward so the visible gaps
  // stay even (shift = the space freed by the smaller slides between it and the centre).
  render() {
    this.frame = 0;
    const middle = this.track.scrollLeft + this.track.clientWidth / 2;
    const { width, step, shrink } = this;
    let closest = 0;
    let closestDistance = Infinity;

    this.cards.forEach((card, index) => {
      const distance = (this.centers[index] - middle) / step;
      const amount = Math.abs(distance);
      const near = Math.min(amount, 1);
      const shift = amount <= 1 ? (width * shrink * amount * amount) / 2 : width * shrink * (amount - 0.5);
      card.style.transform = `translate3d(${(-Math.sign(distance) * shift).toFixed(2)}px, 0, 0) scale(${(1 - shrink * near).toFixed(4)})`;
      card.style.setProperty('--p', (1 - near).toFixed(3));
      if (amount < closestDistance) {
        closestDistance = amount;
        closest = index;
      }
    });

    if (closest !== this.active) this.setActive(closest);
  }

  setActive(index) {
    this.active = index;
    // A video brought back to the centre starts over (otherwise a half-watched one could end
    // straight away and, with auto-advance, skip through the reel).
    const video = this.videos[index];
    if (video && video.currentTime > 0) video.currentTime = 0;
    const bar = this.slides[index].querySelector('[data-reel-progress]');
    if (bar) bar.style.transform = 'scaleX(0)';

    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      slide.querySelectorAll('[data-reel-ui]').forEach((ui) => (ui.inert = !isActive));
      // Warm up the neighbours so the next swipe starts playing straight away.
      const video = this.videos[i];
      if (video && Math.abs(i - index) === 1 && video.preload === 'none') video.preload = 'metadata';
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    this.syncPlayback();
  }

  syncPlayback() {
    // Don't pause a video the visitor is watching full screen.
    const fullscreen = document.fullscreenElement || this.videos.find((video) => video?.webkitDisplayingFullscreen);
    if (fullscreen && this.contains(fullscreen)) return;

    const canPlay = this.inView && !document.hidden && (this.autoplay || this.userStarted);
    this.videos.forEach((video, i) => {
      if (!video) return;
      if (i === this.active && canPlay) {
        video.muted = !this.soundOn;
        this.play(i);
        return;
      }
      if (!video.paused) video.pause();
      if (i === this.active) this.slides[i].classList.add('is-paused');
    });
    this.syncLabels();
  }

  play(index) {
    const video = this.videos[index];
    const slide = this.slides[index];
    video.play().catch(() => {
      // The browser blocked sound: carry on muted.
      if (video.muted) return slide.classList.add('is-paused');
      this.soundOn = false;
      video.muted = true;
      this.syncLabels();
      video.play().catch(() => slide.classList.add('is-paused'));
    });
  }

  // Tap on the centred slide: start it (with sound) if it's paused, otherwise toggle sound.
  toggle(index) {
    const video = this.videos[index];
    if (!video) return;
    if (!video.paused) return this.toggleSound();
    this.userStarted = true;
    this.soundOn = true;
    this.syncPlayback();
  }

  toggleSound() {
    const video = this.videos[this.active];
    if (!video) return;
    this.userStarted = true;
    this.soundOn = !this.soundOn;
    video.muted = !this.soundOn;
    if (video.paused) this.play(this.active);
    this.syncLabels();
  }

  syncLabels() {
    const total = this.slides.length;
    this.slides.forEach((slide, i) => {
      const video = this.videos[i];
      const isActive = i === this.active;
      const unmuted = Boolean(isActive && video && this.soundOn && !video.muted);
      slide.classList.toggle('is-unmuted', unmuted);

      const sound = slide.querySelector('[data-reel-sound]');
      sound?.setAttribute('aria-pressed', String(unmuted));
      sound?.setAttribute('aria-label', unmuted ? 'Mute video' : 'Unmute video');

      const hit = slide.querySelector('[data-reel-hit]');
      if (!hit) return;
      let label = `Show video ${i + 1} of ${total}`;
      if (isActive && video) label = video.paused ? 'Play video with sound' : unmuted ? 'Mute video' : 'Unmute video';
      hit.setAttribute('aria-label', label);
    });
  }

  startProgress() {
    cancelAnimationFrame(this.progressFrame);
    const tick = () => {
      const video = this.videos[this.active];
      const bar = this.slides[this.active]?.querySelector('[data-reel-progress]');
      if (!video || !bar) return;
      if (video.duration) bar.style.transform = `scaleX(${video.currentTime / video.duration})`;
      if (!video.paused) this.progressFrame = requestAnimationFrame(tick);
    };
    tick();
  }

  fullscreen(index) {
    const video = this.videos[index];
    if (!video) return;
    this.userStarted = true;
    this.soundOn = true;
    video.muted = false;
    video.controls = true;

    const onChange = () => {
      if (document.fullscreenElement) return;
      exit();
    };
    const exit = () => {
      document.removeEventListener('fullscreenchange', onChange);
      video.controls = false;
      this.syncPlayback();
    };

    if (video.requestFullscreen) {
      document.addEventListener('fullscreenchange', onChange);
      video.requestFullscreen().catch(exit);
    } else if (video.webkitEnterFullscreen) {
      video.addEventListener('webkitendfullscreen', exit, { once: true });
      video.webkitEnterFullscreen();
    }
    this.play(index);
    this.syncLabels();
  }

  bindSlides() {
    this.slides.forEach((slide, index) => {
      slide.querySelector('[data-reel-hit]')?.addEventListener('click', () => {
        if (index === this.active) this.toggle(index);
        else this.goTo(index);
      });
      slide.querySelector('[data-reel-sound]')?.addEventListener('click', () => this.toggleSound());
      slide.querySelector('[data-reel-expand]')?.addEventListener('click', () => this.fullscreen(index));

      const video = this.videos[index];
      if (!video) return;
      video.loop = !this.advance;
      video.addEventListener('play', () => slide.classList.remove('is-paused'));
      video.addEventListener('pause', () => slide.classList.add('is-paused'));
      video.addEventListener('waiting', () => slide.classList.add('is-loading'));
      video.addEventListener('canplay', () => slide.classList.remove('is-loading'));
      video.addEventListener('playing', () => {
        slide.classList.remove('is-loading');
        if (index === this.active) this.startProgress();
      });
      video.addEventListener('ended', () => {
        if (this.advance && index === this.active) this.goTo(this.wrap(index + 1));
      });
    });

    this.addEventListener('shopify:block:select', (event) => {
      const index = this.slides.indexOf(event.target);
      if (index > -1) this.goTo(index);
    });
  }

  bindControls() {
    this.querySelector('[data-reel-prev]')?.addEventListener('click', () => this.goTo(this.wrap(this.active - 1)));
    this.querySelector('[data-reel-next]')?.addEventListener('click', () => this.goTo(this.wrap(this.active + 1)));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.goTo(this.wrap(this.active + (event.key === 'ArrowRight' ? 1 : -1)));
    });
  }

  // Mouse drag with momentum. Touch and trackpads keep native scrolling.
  bindDrag() {
    const track = this.track;
    let pointerId = null;
    let startX = 0;
    let startLeft = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerId = event.pointerId;
      startX = lastX = event.clientX;
      startLeft = track.scrollLeft;
      lastTime = event.timeStamp;
      velocity = 0;
      this.dragged = false;
    });

    track.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const delta = event.clientX - startX;
      if (!this.dragged) {
        if (Math.abs(delta) < 6) return;
        this.dragged = true;
        track.setPointerCapture(pointerId);
        this.classList.add('is-dragging');
      }
      const elapsed = Math.max(event.timeStamp - lastTime, 1);
      velocity = 0.7 * ((event.clientX - lastX) / elapsed) + 0.3 * velocity;
      lastX = event.clientX;
      lastTime = event.timeStamp;
      track.scrollLeft = startLeft - delta;
    });

    const release = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!this.dragged) return;
      // Project where the flick would carry the reel, then settle on the nearest slide.
      const momentum = event.timeStamp - lastTime > 80 ? 0 : velocity * 280;
      const projected = track.scrollLeft + track.clientWidth / 2 - momentum;
      const distances = this.centers.map((center) => Math.abs(center - projected));
      this.settle(distances.indexOf(Math.min(...distances)));
      setTimeout(() => (this.dragged = false));
    };
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);

    // The click that ends a drag shouldn't also toggle or select a slide.
    track.addEventListener(
      'click',
      (event) => {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  // Scroll to a slide with snapping still off, then turn snapping back on once there,
  // so re-enabling it can't make the reel jump.
  settle(index) {
    const left = this.centers[index] - this.track.clientWidth / 2;
    const done = () => {
      clearTimeout(this.settleTimer);
      this.track.removeEventListener('scrollend', done);
      this.classList.remove('is-dragging');
    };
    if (Math.abs(this.track.scrollLeft - left) < 1) return done();
    this.track.addEventListener('scrollend', done, { once: true });
    this.settleTimer = setTimeout(done, 700);
    this.goTo(index);
  }

  wrap(index) {
    const total = this.slides.length;
    return (index + total) % total;
  }

  goTo(index, behavior = reducedMotion.matches ? 'auto' : 'smooth') {
    if (!this.slides[index]) return;
    this.track.scrollTo({ left: this.centers[index] - this.track.clientWidth / 2, behavior });
  }
}

/* Click-to-play video: swaps the cover button for the player in the <template>. */
class PlpVideo extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-video-play]')?.addEventListener('click', () => this.load(), { once: true });
  }

  load() {
    const cover = this.querySelector('[data-video-play]');
    const player = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!cover || !player) return;

    cover.replaceWith(player);
    if (player.tagName === 'VIDEO') player.play().catch(() => {});
    else player.focus?.();
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('plp-rotator', PlpRotator);
define('plp-slider', PlpSlider);
define('plp-reel', PlpReel);
define('plp-video', PlpVideo);
